import React from "react";
import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { Badge, FilterChip } from "../../../ui";
import type { TraceEvaluation } from "../types";
import { EvaluationExplanation } from "./EvaluationExplanation";
import { EvaluationSources } from "./EvaluationSources";

/**
 * Pass / fail / not-scored. The left bar is the card's severity cue, and an
 * evaluation with no score has not failed — it renders `—`, so a red bar would
 * be asserting a result that does not exist.
 */
function scoreState(score: number | null): "pass" | "fail" | "unscored" {
  if (score === null) return "unscored";
  return score >= 0.5 ? "pass" : "fail";
}

const ACCENT: Record<ReturnType<typeof scoreState>, string> = {
  pass: "border-l-success",
  fail: "border-l-destructive",
  unscored: "border-l-border",
};

interface EvaluationCardProps {
  evaluation: TraceEvaluation;
  copiedExplanationId: string | null;
  copiedSourceId: string | null;
  onCopyExplanation: (id: string) => void;
  onCopySource: (id: string) => void;
  onViewSubTrace: (data: Record<string, any>, evaluatorName: string | null) => void;
  onSourceClick: (type: string, id: string) => void;
}

/**
 * Renders a single evaluation card: evaluator name, criteria, score, explanation, sources.
 */
export function EvaluationCard({
  evaluation: ev,
  copiedExplanationId,
  copiedSourceId,
  onCopyExplanation,
  onCopySource,
  onViewSubTrace,
  onSourceClick,
}: EvaluationCardProps) {
  const state = scoreState(ev.score);

  return (
    <div
      className={`border border-border border-l-4 ${ACCENT[state]} rounded-lg p-4 space-y-2 bg-card`}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <span className="text-sm font-semibold text-foreground block">
            {ev.evaluator_name ?? "—"}
          </span>
          {ev.criteria && (
            <div className="mt-1.5" title={ev.criteria}>
              <FilterChip
                icon={<CheckCircleIcon className="h-4 w-4" />}
                className="px-3 py-1.5 text-sm max-w-full"
              >
                <span className="font-medium">
                  {ev.criteria.length > 100 ? `${ev.criteria.slice(0, 100)}…` : ev.criteria}
                </span>
              </FilterChip>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {ev.cached && (
            <Badge
              variant="success"
              title={
                ev.c_cached_tokens != null
                  ? `${ev.c_cached_tokens.toLocaleString()} cached prefix tokens reused`
                  : "Reused a recently-cached prompt prefix"
              }
            >
              Cached
            </Badge>
          )}
          {ev.sub_trace && (
            <button
              onClick={() => onViewSubTrace(ev.sub_trace!, ev.evaluator_name)}
              className="px-3 py-1.5 text-sm font-medium text-muted-foreground bg-muted hover:bg-accent rounded-md transition-colors"
            >
              View Sub-Trace
            </button>
          )}
          <span
            className={`text-xs font-bold px-2 py-0.5 rounded ${
              state === "pass"
                ? "bg-success/15 text-success"
                : state === "fail"
                  ? "bg-destructive/15 text-destructive"
                  : "bg-muted text-muted-foreground"
            }`}
          >
            {ev.score !== null ? ev.score.toFixed(2) : "—"}
          </span>
        </div>
      </div>

      {ev.explanation && (
        <EvaluationExplanation
          explanation={ev.explanation}
          evaluationId={ev.id}
          copiedId={copiedExplanationId}
          onCopy={onCopyExplanation}
        />
      )}

      {ev.sources && ev.sources.length > 0 && (
        <EvaluationSources
          sources={ev.sources}
          copiedSourceId={copiedSourceId}
          onSourceClick={onSourceClick}
          onCopyId={onCopySource}
        />
      )}
    </div>
  );
}
